/**
 * Gift Finder — standalone Shopify OS 2.0 section.
 *
 * Matching:
 *  Single curated combination tag: gift:<purpose>:<recipient>
 *  AND optional product-price filter (step 3).
 *  Example: gift:appreciation:host, gift:joy:serenity, gift:wow:self.
 *
 * Card source markup is kept inside a <template> so that it never
 * appears on the welcome screen and does not duplicate live form IDs.
 */
(() => {
  'use strict';

  class GiftFinder {
    constructor(root) {
      this.root = root;
      this.id = root.dataset.sectionId || 'gift-finder';
      this.hero = root.querySelector('[data-gf-hero]');
      this.heroVideo = this.hero?.querySelector('video');
      this.startButton = root.querySelector('[data-gf-start]');
      this.questionView = root.querySelector('[data-gf-question-view]');
      this.heading = root.querySelector('[data-gf-question-title]');
      this.optionsEl = root.querySelector('[data-gf-options]');
      this.backButton = root.querySelector('[data-gf-back]');
      this.nextButton = root.querySelector('[data-gf-next]');
      this.stepper = root.querySelector('[data-gf-stepper]');
      this.results = root.querySelector('[data-gf-results]');
      this.resultGrid = root.querySelector('[data-gf-results-grid]');
      this.resultCount = root.querySelector('[data-gf-result-count]');
      this.emptyMessage = root.querySelector('[data-gf-empty]');
      this.restartButton = root.querySelector('[data-gf-restart]');
      this.cardSource = root.querySelector('[data-gf-product-source]');
      this.config = this.parseJSON('[data-gf-config]');
      this.catalogue = this.parseJSON('[data-gf-products]');
      this.steps = ['purpose', 'recipient', 'budget'];
      this.currentStep = 0;
      this.answers = {};
      this.answerLabels = {};

      this.ready = Boolean(
        this.hero && this.startButton && this.questionView &&
        this.heading && this.optionsEl && this.backButton &&
        this.nextButton && this.results && this.resultGrid &&
        this.config && this.catalogue &&
        this.steps.every((step) => Array.isArray(this.config.options?.[step]))
      );

      if (!this.ready) {
        console.warn('[Gift Finder] Missing markup or configuration.', root);
        return;
      }

      this.startButton.addEventListener('click', () => this.start());
      this.backButton.addEventListener('click', () => this.goBack());
      this.nextButton.addEventListener('click', () => this.goNext());
      this.restartButton?.addEventListener('click', () => this.restart());
    }

    parseJSON(selector) {
      const element = this.root.querySelector(selector);
      if (!element) return null;
      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error('[Gift Finder] Invalid JSON:', selector, error);
        return null;
      }
    }

    static normalized(value) {
      return String(value == null ? '' : value).trim().toLowerCase();
    }

    start() {
      this.answers = {};
      this.answerLabels = {};
      this.currentStep = 0;
      // The video is needed only on the welcome screen.
      this.heroVideo?.pause();
      this.hero.hidden = true;
      this.results.hidden = true;
      this.questionView.hidden = false;
      if (this.stepper) this.stepper.hidden = !this.config.showStepper;
      this.renderStep();
    }

    optionLabel(option, step) {
      const label = String(option.label || '');
      // Theme Editor can use {budget} in step-3 option labels.
      return step === 'budget'
        ? label.replace(/\{budget\}/gi, this.config.budgetDisplay || '')
        : label;
    }

    renderStep() {
      const step = this.steps[this.currentStep];
      const options = this.config.options[step] || [];
      const titles = this.config.headings || {};

      this.heading.textContent = titles[step] || '';
      this.optionsEl.replaceChildren();
      this.optionsEl.dataset.gfStep = step;
      this.optionsEl.dataset.gfColumns = String(
        step === 'recipient' ? 5 : 3
      );

      options.forEach((option, index) => {
        const value = GiftFinder.normalized(option.value);
        if (!value) return;

        const label = document.createElement('label');
        label.className = 'gf__option';
        if (this.answers[step] === value) label.classList.add('is-selected');

        const input = document.createElement('input');
        input.type = 'radio';
        input.className = 'gf__option-input';
        input.name = `gift-${this.id}-${step}`;
        input.value = value;
        input.checked = this.answers[step] === value;
        input.setAttribute('aria-label', this.optionLabel(option, step));

        const media = document.createElement('span');
        media.className = 'gf__option-media';
        if (option.image) {
          const image = document.createElement('img');
          image.src = option.image;
          image.alt = '';
          image.loading = index < 3 ? 'eager' : 'lazy';
          image.decoding = 'async';
          media.appendChild(image);
        } else {
          media.classList.add('gf__option-media--placeholder');
        }

        const selectedMark = document.createElement('span');
        selectedMark.className = 'gf__selected-mark';
        selectedMark.setAttribute('aria-hidden', 'true');
        selectedMark.textContent = '✓';
        media.appendChild(selectedMark);

        const name = document.createElement('span');
        name.className = 'gf__option-name';
        name.textContent = this.optionLabel(option, step);

        label.append(input, media, name);

        if (option.description) {
          const description = document.createElement('span');
          description.className = 'gf__option-description';
          description.textContent = option.description;
          label.appendChild(description);
        }

        input.addEventListener('change', () => {
          // Earlier answers may be changed; require the later choices again.
          this.answers[step] = value;
          this.answerLabels[step] = this.optionLabel(option, step);
          for (let i = this.currentStep + 1; i < this.steps.length; i += 1) {
            delete this.answers[this.steps[i]];
            delete this.answerLabels[this.steps[i]];
          }
          this.optionsEl.querySelectorAll('.gf__option').forEach((card) => {
            card.classList.toggle('is-selected', card === label);
          });
          this.updateNavigation();
          this.renderStepper();
        });

        this.optionsEl.appendChild(label);
      });

      this.backButton.hidden = this.currentStep === 0;
      this.nextButton.textContent = this.currentStep === 2
        ? (this.config.confirmLabel || 'Confirm')
        : (this.config.continueLabel || 'Continue');
      this.updateNavigation();
      this.renderStepper();
    }

    updateNavigation() {
      const step = this.steps[this.currentStep];
      this.nextButton.disabled = !this.answers[step];
    }

    renderStepper() {
      if (!this.stepper) return;
      this.stepper.querySelectorAll('[data-gf-stepper-item]').forEach((element) => {
        const index = Number(element.dataset.gfStepperItem);
        const step = this.steps[index];
        const isCurrent = index === this.currentStep;
        const isCompleted = index < this.currentStep;
        element.classList.toggle('is-current', isCurrent);
        element.classList.toggle('is-completed', isCompleted);
        const answer = element.querySelector('[data-gf-step-answer]');
        if (answer) answer.textContent = this.answerLabels[step] || '';
        element.setAttribute('aria-current', isCurrent ? 'step' : 'false');
      });
    }

    goBack() {
      if (this.currentStep <= 0) return;
      this.currentStep -= 1;
      this.renderStep();
      this.heading.focus({ preventScroll: true });
    }

    goNext() {
      if (!this.answers[this.steps[this.currentStep]]) return;
      if (this.currentStep === this.steps.length - 1) {
        this.showResults();
        return;
      }
      this.currentStep += 1;
      this.renderStep();
      this.questionView.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.heading.focus({ preventScroll: true });
    }

    matches(product) {
      const tags = new Set((Array.isArray(product.tags) ? product.tags : [])
        .map(GiftFinder.normalized));
      // A single compound tag controls this exact purpose/recipient pair.
      // It will not accidentally include other combinations on the product.
      const combinationTag = `gift:${this.answers.purpose}:${this.answers.recipient}`;
      if (!tags.has(combinationTag)) return false;

      if (!this.config.includeUnavailable && product.available === false) {
        return false;
      }

      const price = Number(product.priceSubunits);
      const threshold = Number(this.config.budgetSubunits);
      if (this.answers.budget === 'under') return price <= threshold;
      if (this.answers.budget === 'over') return price > threshold;
      return this.answers.budget === 'all';
    }

    getSourceCard(productId) {
      if (!(this.cardSource instanceof HTMLTemplateElement)) return null;
      // Avoid CSS selector escaping/ID matching bugs on different product IDs.
      return Array.from(
        this.cardSource.content.querySelectorAll('[data-gf-source-card]')
      ).find((element) => element.dataset.productId === String(productId)) || null;
    }

    static clearCardRevealStyles(root) {
      // card-product-enhanced may pre-render inline opacity:0/transform.
      const styled = [root, ...root.querySelectorAll('[style], [data-product-card-transition], .scroll-trigger, .scroll-trigger--offscreen')];
      styled.forEach((element) => {
        if (element.style?.opacity === '0') element.style.removeProperty('opacity');
        if (element.style?.transform) element.style.removeProperty('transform');
        if (element.style?.animation) element.style.removeProperty('animation');
        element.removeAttribute('data-product-card-transition');
        element.classList.remove('scroll-trigger', 'scroll-trigger--offscreen', 'animate--slide-in', 'animate--fade-in');
      });
      root.removeAttribute('hidden');
      root.removeAttribute('data-gf-source-card');
    }

    showResults() {
      const products = Array.isArray(this.catalogue.products)
        ? this.catalogue.products
        : [];
      const selected = products.filter((product) => this.matches(product))
        .slice(0, Math.max(1, Number(this.config.maxProducts) || 12));

      this.resultGrid.replaceChildren();
      selected.forEach((product) => {
        const source = this.getSourceCard(product.id);
        if (!source) return;
        const card = source.cloneNode(true);
        GiftFinder.clearCardRevealStyles(card);
        card.classList.add('gf__result-card');
        this.resultGrid.appendChild(card);
      });

      const count = this.resultGrid.children.length;
      this.emptyMessage.hidden = count !== 0;
      this.resultGrid.hidden = count === 0;
      if (this.resultCount) {
        this.resultCount.textContent = count
          ? `${count} ${count === 1 ? 'suggestion' : 'suggestions'} for your selection`
          : '';
      }

      this.questionView.hidden = true;
      if (this.stepper) this.stepper.hidden = true;
      this.results.hidden = false;

      this.root.dispatchEvent(new CustomEvent('gift-finder:results', {
        bubbles: true,
        detail: {
          purpose: this.answers.purpose,
          recipient: this.answers.recipient,
          budget: this.answers.budget,
          productIds: selected.map((product) => product.id),
        },
      }));

      this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.results.querySelector('[data-gf-results-heading]')
        ?.focus({ preventScroll: true });
    }

    restart() {
      this.answers = {};
      this.answerLabels = {};
      this.currentStep = 0;
      this.resultGrid.replaceChildren();
      this.results.hidden = true;
      this.questionView.hidden = true;
      if (this.stepper) this.stepper.hidden = true;
      this.hero.hidden = false;
      // Restore the looping welcome video after Start again.
      if (this.heroVideo && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.heroVideo.play().catch(() => {});
      }
      this.hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.startButton.focus({ preventScroll: true });
    }
  }

  function initGiftFinders(context = document) {
    const roots = [];
    if (context.matches?.('[data-gift-finder]')) roots.push(context);
    roots.push(...context.querySelectorAll('[data-gift-finder]'));
    roots.forEach((root) => {
      if (root.dataset.giftFinderInitialized === 'true') return;
      const instance = new GiftFinder(root);
      if (instance.ready) root.dataset.giftFinderInitialized = 'true';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initGiftFinders());
  } else {
    initGiftFinders();
  }
  document.addEventListener('shopify:section:load', (event) => {
    initGiftFinders(event.target);
  });
})();
